import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  hasDirectClaimAccessFromDuplicate,
  skipClaimRolePrompt,
  wasClaimRolePromptSkipped
} from '../utils/claimAccessUtils';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';

/**
 * Navigate to an existing claim, prompting to set handler/assistant when the
 * current customer is not already on the file.
 */
export function useViewExistingClaim(makeAuthenticatedRequest, customer) {
  const navigate = useNavigate();
  const [pendingClaim, setPendingClaim] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigateAfterRef = useRef(true);
  const onUpdatedRef = useRef(null);

  const shouldPrompt = useCallback(
    (claim) => {
      if (!claim?.id || customer?.id == null) return false;
      if (hasDirectClaimAccessFromDuplicate(claim, customer.id)) return false;
      if (wasClaimRolePromptSkipped(customer.id, claim.id)) return false;
      return true;
    },
    [customer?.id]
  );

  const viewClaim = useCallback(
    (claim) => {
      if (!claim?.id) return;

      if (!shouldPrompt(claim)) {
        navigate(`/claims/${claim.id}`);
        return;
      }

      navigateAfterRef.current = true;
      onUpdatedRef.current = null;
      setPendingClaim(claim);
    },
    [navigate, shouldPrompt]
  );

  const promptIfNeeded = useCallback(
    (claim, options = {}) => {
      if (!shouldPrompt(claim)) return;
      navigateAfterRef.current = false;
      onUpdatedRef.current = options.onUpdated || null;
      setPendingClaim(claim);
    },
    [shouldPrompt]
  );

  const finish = useCallback(
    (claimId, { didUpdate } = {}) => {
      setPendingClaim(null);
      if (didUpdate && typeof onUpdatedRef.current === 'function') {
        onUpdatedRef.current();
      }
      if (navigateAfterRef.current && claimId) {
        navigate(`/claims/${claimId}`);
      }
    },
    [navigate]
  );

  const skipPrompt = useCallback(() => {
    if (pendingClaim?.id && customer?.id) {
      skipClaimRolePrompt(customer.id, pendingClaim.id);
    }
    const claimId = pendingClaim?.id;
    finish(claimId);
  }, [customer?.id, finish, pendingClaim]);

  const saveAssignment = useCallback(
    async ({ role, counterpartId }) => {
      if (!pendingClaim?.id || !customer?.id || !role) {
        setPendingClaim(null);
        return;
      }

      setSaving(true);
      try {
        const claimData = await makeAuthenticatedRequest(`/claims/${pendingClaim.id}`);

        if (!claimData.success || !claimData.data) {
          toast.error(claimData.message || 'Failed to load claim data');
          setPendingClaim(null);
          return;
        }

        const currentClaim = claimData.data;
        const handlerId = role === 'handler' ? customer.id : counterpartId || null;
        const assistantId = role === 'assistant' ? customer.id : counterpartId || null;

        const updatePayload = {
          claimant_id: currentClaim.claimant_id || null,
          case_type: currentClaim.case_type || 'Workers Compensation',
          claim_number: currentClaim.claim_number || '',
          date_of_injury: currentClaim.date_of_injury || null,
          ct_start_date: currentClaim.ct_start_date || null,
          ct_end_date: currentClaim.ct_end_date || null,
          diagnosis: currentClaim.diagnosis || null,
          employer_insured: currentClaim.employer_insured || null,
          contact_claims_handler_id: handlerId,
          adjusters_assistant_id: assistantId,
          nurse_case_manager_id: currentClaim.nurse_case_manager_id || null,
          field_case_manager_id: currentClaim.field_case_manager_id || null
        };

        const response = await makeAuthenticatedRequest(`/claims/${pendingClaim.id}`, {
          method: 'PUT',
          body: JSON.stringify(updatePayload)
        });

        if (response.success) {
          toast.success(
            role === 'handler'
              ? "You're now the claims handler on this file"
              : "You're now the adjuster's assistant on this file"
          );
          const claimId = pendingClaim.id;
          finish(claimId, { didUpdate: true });
        } else {
          toast.error(response.message || 'Failed to update claim');
        }
      } catch (error) {
        console.error('Error updating claim:', error);
        toast.error(getCustomerPortalErrorMessage(error, 'Failed to update claim'));
      } finally {
        setSaving(false);
      }
    },
    [customer?.id, finish, makeAuthenticatedRequest, pendingClaim]
  );

  return {
    viewClaim,
    promptIfNeeded,
    pendingClaim,
    saveAssignment,
    skipPrompt,
    saving,
    confirmAdjusterAssignment: saveAssignment,
    cancelAdjusterPrompt: skipPrompt
  };
}
